import { useImperativeHandle, forwardRef, useEffect } from "react";
import { createPortal } from 'react-dom';
import { Col, Row, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export default forwardRef(function Timer({ item, setBackgroundColor, setIsPlaying, isPlaying, time, setTime, getMeeting, isTimerManager, onTimerStateChange, isLocked = false, controlsTarget = null }, ref) {
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector(x => x.auth);

  // const intervalRef = useRef(null);

  // Play/Pause toggle
  const togglePlayPause = () => {
    setIsPlaying(prev => {
      const nextIsPlaying = !prev;
      onTimerStateChange?.(nextIsPlaying);
      return nextIsPlaying;
    });
  };

  // Stop handler
  const handleStop = async () => {
    setIsPlaying(false);
    onTimerStateChange?.(false);
    // clearInterval(intervalRef.current); // Immediately stop the timer interval

    try {
      let timerData;
      // console.log('Saving time record:', time);
      if (item.timer) {
        // Timer exists, update it
        const updateResponse = await fetch(`${url}/timer/${item.agendaItemId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            event: item.agendaEvent.name,
            name: `${item.assignedUser?.firstName || 'TBA'} ${item.assignedUser?.lastName || ''}`,
            time: time, // Use the current time state
          }),
        });

        if (updateResponse.status === 200 || updateResponse.status === 201) {
          timerData = await updateResponse.json();
        } else {
          throw new Error('Failed to update time record');
        }
      } else {
        // Timer does not exist, create a new one
        const createResponse = await fetch(`${url}/timer/${item.agendaItemId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            event: item.agendaEvent.name,
            name: `${item.assignedUser?.firstName || 'TBA'} ${item.assignedUser?.lastName || ''}`,
            time: time, // Use the current time state
          }),
        });

        if (createResponse.status === 200 || createResponse.status === 201) {
          timerData = await createResponse.json();
        } else {
          throw new Error('Failed to save time record');
        }
      }

      // Update the timer state with the saved data
      setTime(timerData.time);
    } catch (err) {
      toast.error('' + err, { position: 'bottom-left' });
    }
    getMeeting();
  };

  // Expose handleStop to parent via ref
  useImperativeHandle(ref, () => ({
    handleStop
  }));

  // Timer interval effect
  // useEffect(() => {
  //   if (isPlaying) {
  //     intervalRef.current = setInterval(() => {
  //       setTime(prevTime => prevTime + 1);
  //     }, 1000);
  //   } else {
  //     clearInterval(intervalRef.current);
  //   }
  //   return () => clearInterval(intervalRef.current);
  // }, [isPlaying, setTime]);

  // Background color effect
  useEffect(() => {
    const { green, amber, red } = item?.agendaEvent || {};
    const convertToSeconds = (timeString) => {
      if (!timeString) return null;
      const [minutes, seconds] = timeString.split(':').map(Number);
      return minutes * 60 + seconds;
    };
    const greenTime = convertToSeconds(green);
    const amberTime = convertToSeconds(amber);
    const redTime = convertToSeconds(red);


    if (redTime !== null && time >= redTime + 30) {
      setBackgroundColor("#dc3544"); // Disqualification (red + 30s)
    } else if (redTime !== null && time >= redTime) {
      setBackgroundColor("#dc3545"); // Red
    } else if (amberTime !== null && time >= amberTime) {
      setBackgroundColor("#ffc107"); // Amber
    } else if (greenTime !== null && time >= greenTime) {
      setBackgroundColor("#146c43"); // Green
    } else {
      setBackgroundColor("var(--modal-bg)");
    }
  }, [time, item?.agendaEvent, setBackgroundColor]);

  if (!isTimerManager) {
    return null;
  }

  const startActionText = Number(time) > 0 ? 'Resume' : 'Start';
  const buttonTitle = isPlaying ? 'Stop timer' : `${startActionText} timer`;
  const buttonText = isPlaying ? 'Stop' : startActionText;

  const controls = (
    <Row className="agenda-modal-timer-row">
      <Col className="agenda-modal-timer-controls">
        {!isLocked && (
          <Button
            variant="link"
            className={`agenda-modal-timer-button ${isPlaying ? 'agenda-modal-timer-button--pause' : 'agenda-modal-timer-button--start'}`}
            onClick={isPlaying ? handleStop : togglePlayPause}
            title={buttonTitle}
            aria-label={buttonTitle}
          >
            <i className={`bi ${isPlaying ? 'bi-stop-fill' : 'bi-play-fill'} agenda-modal-timer-button-icon`} aria-hidden="true" />
            <span className="agenda-modal-timer-button-text">{buttonText}</span>
          </Button>
        )}
      </Col>
    </Row>
  );
  return controlsTarget ? createPortal(controls, controlsTarget) : controls;
}
)